import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import SEOHead from '@/components/common/SEOHead';
import Link from 'next/link';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { motion } from 'framer-motion';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = context;
  
  return {
    props: {
      ...(await serverSideTranslations(locale || 'en', [
        'common',
        'header',
        'footer',
        'copyright',
      ])),
    },
  };
};

const CopyrightPage = () => {
  const { t } = useTranslation('copyright');

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <SEOHead
        pageKey="copyright"
        customConfig={{
          title: t('pageTitle'),
          description: t('pageDescription'),
          keywords: 'copyright policy, DMCA, intellectual property, downloadmod',
          url: '/copyright'
        }}
      />

      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 to-primary/10 py-16">
          <div className="container mx-auto px-4 text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {t('heroTitle')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {t('heroSubtitle')}
            </motion.p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {t('mainTitle')}
              </h2>
              
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                {(() => {
                  const content = t('mainContent', { returnObjects: true });
                  if (Array.isArray(content)) {
                    return content.map((paragraph: any, index: number) => (
                      <p key={index} className="leading-relaxed">{String(paragraph)}</p>
                    ));
                  }
                  return <p className="leading-relaxed">{t('mainContent')}</p>;
                })()}
              </div>

              <div className="mt-10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('requestTitle')}
                </h3>
                <div className="space-y-4 text-gray-700 dark:text-gray-300">
                  {(() => {
                    const content = t('requestContent', { returnObjects: true });
                    if (Array.isArray(content)) {
                      return content.map((paragraph: any, index: number) => (
                        <p key={index} className="leading-relaxed">{String(paragraph)}</p>
                      ));
                    }
                    return <p className="leading-relaxed">{t('requestContent')}</p>;
                  })()}
                </div>
              </div>

              <div className="mt-10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('requirementsTitle')}
                </h3>
                <ul className="list-disc pl-6 space-y-3 text-gray-700 dark:text-gray-300">
                  {(() => {
                    const content = t('requirementsList', { returnObjects: true });
                    if (Array.isArray(content)) {
                      return content.map((item: any, index: number) => (
                        <li key={index} className="leading-relaxed">{String(item)}</li>
                      ));
                    }
                    return <li className="leading-relaxed">{t('requirementsList')}</li>;
                  })()}
                </ul>
              </div>

              <div className="mt-10 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('contactNote')}
                </p>
                <Link 
                  href="/contact" 
                  className="inline-flex items-center px-6 py-3 bg-primary text-white font-medium rounded-md hover:bg-primary/90 transition-colors"
                >
                  {t('contactLinkText')}
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CopyrightPage;
